import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BadgeCheck,
  Globe2,
  KeyRound,
  LogOut,
  MailWarning,
  Trash2,
  Wallet,
} from "lucide-react";
import {
  DELETION_GRACE_DAYS,
  isUnlimited,
  planFor,
  type PlanKey,
} from "@marketing/shared";
import { api, apiErrorMessage, clearTokens, unwrap } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { Badge, Card, Notice, PageHeader, Spinner } from "@/components/ui";
import { ErrorBox } from "./LoginPage";

// =============================================================================
// The account: who you are, what you are on, what you have paid for.
//
// Reached from the name and email in the sidebar, which previously said
// something and did nothing. Four questions in the order people ask them:
// who am I signed in as, what workspace is this, what does my plan allow, and
// what has been charged.
//
// Deliberately read-only apart from the two security actions. Editing a
// workspace's name or country belongs where that workspace is managed; an
// account page that also edits things becomes the page where everything
// lives and nothing is findable.
// =============================================================================

type Workspace = {
  id: string;
  name: string;
  plan: PlanKey;
  billingInterval: string;
  country: string;
  timezone: string;
  createdAt: string;
  role: string;
};

type Credit = {
  id: string;
  change: number;
  reason: string;
  balanceAfter: number;
  expiresAt: string | null;
  createdAt: string;
  paddleTransactionId: string | null;
};

type History = {
  credits: Credit[];
  balance: number;
  events: Array<{ id: string; eventType: string; createdAt: string }>;
  subscription: {
    plan: PlanKey;
    interval: string;
    status: string | null;
    currentPeriodEnd: string | null;
  };
};

export function AccountPage() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const workspace = useQuery({
    queryKey: ["workspace", "current"],
    queryFn: () => unwrap<Workspace>(api.get("/workspaces/current")),
  });

  const history = useQuery({
    queryKey: ["billing", "history"],
    queryFn: () => unwrap<History>(api.get("/billing/history")),
  });

  const [confirming, setConfirming] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");

  // The grace period comes from @marketing/shared rather than a 30 written
  // here: the server schedules the purge by it and the public /data-deletion
  // page states it, so a second copy is a promise waiting to disagree.
  const pendingDeletion = me.data?.deletionRequestedAt ?? null;
  const pendingPurgeAt = pendingDeletion
    ? new Date(
        new Date(pendingDeletion).getTime() + DELETION_GRACE_DAYS * 24 * 3_600_000,
      )
    : null;

  const resendVerification = useMutation({
    mutationFn: () => unwrap<{ sent: boolean }>(api.post("/auth/verify-email/resend", {})),
    onSuccess: (r) => setNotice(r.sent ? t("account.verifySent") : t("account.verifyFailed")),
    onError: (err) => setError(apiErrorMessage(err, t("errors.generic"))),
  });

  /**
   * Changing a password sends the same emailed link as forgetting one.
   *
   * There is no separate "current password, new password" form, and that is
   * the safer arrangement rather than a shortcut: the link proves control of
   * the mailbox, which is what matters if the session was left open on a
   * borrowed machine — exactly the case where someone is standing at this
   * page wanting to change the password.
   */
  const changePassword = useMutation({
    mutationFn: () =>
      unwrap(api.post("/auth/forgot-password", { email: me.data?.email })),
    onSuccess: () => setNotice(t("account.resetSent", { email: me.data?.email })),
    onError: (err) => setError(apiErrorMessage(err, t("errors.generic"))),
  });

  /**
   * Deleting the account.
   *
   * A password field rather than a window.confirm, unlike the two actions
   * above. Those are recoverable — a reset link arrives, a session is created
   * again. This destroys live page credentials on the spot and schedules an
   * irreversible purge, so the thing being tested is that the person at the
   * keyboard is the account holder, which a confirm dialog cannot do.
   */
  const requestDeletion = useMutation({
    mutationFn: () =>
      unwrap<{ purgeAt: string; pagesDisconnected: number }>(
        api.post("/auth/account/delete", { password: deletePassword }),
      ),
    onSuccess: (r) => {
      setDeletePassword("");
      setConfirming(false);
      setNotice(
        t("account.deleteScheduled", { date: new Date(r.purgeAt).toLocaleDateString() }) +
          (r.pagesDisconnected > 0
            ? " " + t("account.deletePagesGone", { count: r.pagesDisconnected })
            : ""),
      );
      // The countdown and the cancel button live on /auth/me.
      void me.refetch();
    },
    onError: (err) => setError(apiErrorMessage(err, t("errors.generic"))),
  });

  const cancelDeletion = useMutation({
    mutationFn: () => unwrap(api.post("/auth/account/delete/cancel", {})),
    onSuccess: () => {
      setNotice(t("account.deleteCancelled"));
      void me.refetch();
    },
    onError: (err) => setError(apiErrorMessage(err, t("errors.generic"))),
  });

  const signOutEverywhere = useMutation({
    mutationFn: () => unwrap<{ revoked: number }>(api.post("/auth/sign-out-everywhere", {})),
    onSuccess: () => {
      // This session was revoked too, so staying on the page would mean the
      // next request silently 401s. Out, deliberately and immediately.
      clearTokens();
      window.location.assign("/login");
    },
    onError: (err) => setError(apiErrorMessage(err, t("errors.generic"))),
  });

  const user = me.data;
  if (!user) return <p className="text-sm text-muted">{t("errors.loading")}</p>;

  const ws = workspace.data;
  const plan = planFor(ws?.plan ?? "free");

  return (
    <div className="space-y-6">
      <PageHeader title={t("account.title")} subtitle={t("account.subtitle")} />

      {notice ? <Notice tone="success">{notice}</Notice> : null}
      {error ? <ErrorBox>{error}</ErrorBox> : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={t("account.you")}>
          <Row label={t("account.name")} value={user.name} />
          <Row
            label={t("account.email")}
            value={
              <span className="flex flex-wrap items-center gap-2">
                {user.email}
                {user.emailVerifiedAt ? (
                  <Badge tone="success">
                    <BadgeCheck size={12} className="mr-1 inline" />
                    {t("account.verified")}
                  </Badge>
                ) : (
                  <Badge tone="warning">
                    <MailWarning size={12} className="mr-1 inline" />
                    {t("account.unverified")}
                  </Badge>
                )}
              </span>
            }
          />
          {!user.emailVerifiedAt ? (
            <button
              onClick={() => resendVerification.mutate()}
              disabled={resendVerification.isPending}
              className="btn-secondary mt-3"
            >
              {resendVerification.isPending ? <Spinner /> : null}
              {t("account.sendVerification")}
            </button>
          ) : null}
          <Row
            label={t("account.language")}
            value={
              <span className="flex items-center gap-2">
                <Globe2 size={14} className="text-muted" />
                {i18n.language}
              </span>
            }
          />
        </Card>

        <Card title={t("account.workspace")}>
          {ws ? (
            <>
              <Row label={t("account.workspaceName")} value={ws.name} />
              <Row label={t("account.role")} value={t(`roles.${ws.role}`, ws.role)} />
              <Row label={t("account.country")} value={ws.country} />
              <Row label={t("account.timezone")} value={ws.timezone} />
              <Row
                label={t("account.memberSince")}
                value={new Date(ws.createdAt).toLocaleDateString()}
              />
            </>
          ) : (
            <p className="text-sm text-muted">{t("errors.loading")}</p>
          )}
        </Card>
      </div>

      <Card
        title={t("account.plan")}
        actions={
          <Link to="/billing" className="btn-secondary">
            {t("account.managePlan")}
          </Link>
        }
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat
            label={t("account.currentPlan")}
            value={plan.name}
            hint={
              history.data?.subscription.status
                ? t(`account.status.${history.data.subscription.status}`, {
                    defaultValue: history.data.subscription.status,
                  })
                : t("account.noSubscription")
            }
          />
          <Stat
            label={t("account.monthlyPosts")}
            value={
              isUnlimited(plan.postsPerMonth)
                ? t("billing.unlimited")
                : String(plan.postsPerMonth)
            }
            hint={
              plan.previousPostsPerMonth
                ? t("account.wasBefore", { count: plan.previousPostsPerMonth })
                : undefined
            }
          />
          <Stat
            label={t("account.extraPosts")}
            value={String(history.data?.balance ?? 0)}
            hint={t("account.fromPacks")}
          />
        </div>

        {history.data?.subscription.currentPeriodEnd ? (
          <p className="mt-4 text-sm text-muted">
            {t("account.renews", {
              date: new Date(history.data.subscription.currentPeriodEnd).toLocaleDateString(),
            })}
          </p>
        ) : null}
      </Card>

      <Card title={t("account.transactions")}>
        {history.isLoading ? (
          <p className="text-sm text-muted">{t("errors.loading")}</p>
        ) : !history.data?.credits.length && !history.data?.events.length ? (
          <p className="text-sm text-muted">{t("account.noTransactions")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                  <th className="pb-2 font-semibold">{t("account.date")}</th>
                  <th className="pb-2 font-semibold">{t("account.what")}</th>
                  <th className="pb-2 text-right font-semibold">{t("account.change")}</th>
                  <th className="pb-2 text-right font-semibold">{t("account.balance")}</th>
                </tr>
              </thead>
              <tbody>
                {history.data!.credits.map((row) => (
                  <tr key={row.id} className="border-b border-border/50 last:border-0">
                    <td className="py-2 text-muted">
                      {new Date(row.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-2">
                      {t(`account.reason.${row.reason}`, { defaultValue: row.reason })}
                    </td>
                    <td
                      className={`py-2 text-right tabular-nums ${
                        row.change >= 0 ? "text-success" : "text-muted"
                      }`}
                    >
                      {row.change >= 0 ? `+${row.change}` : row.change}
                    </td>
                    <td className="py-2 text-right tabular-nums text-muted">{row.balanceAfter}</td>
                  </tr>
                ))}
                {history.data!.events.map((row) => (
                  <tr key={row.id} className="border-b border-border/50 last:border-0">
                    <td className="py-2 text-muted">
                      {new Date(row.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-2">
                      {t(`account.event.${row.eventType}`, { defaultValue: row.eventType })}
                    </td>
                    <td className="py-2 text-right text-muted">—</td>
                    <td className="py-2 text-right text-muted">—</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Paddle holds the invoices. Reproducing a VAT receipt here is
                how a number ends up disagreeing with the customer's
                accountant. */}
            <p className="mt-3 flex items-center gap-2 text-xs text-muted">
              <Wallet size={13} /> {t("account.receiptsNote")}
            </p>
          </div>
        )}
      </Card>

      <Card title={t("account.security")}>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => changePassword.mutate()}
            disabled={changePassword.isPending}
            className="btn-secondary"
          >
            {changePassword.isPending ? <Spinner /> : <KeyRound size={15} />}
            {t("account.changePassword")}
          </button>
          <button
            onClick={() => {
              if (window.confirm(t("account.confirmSignOutAll"))) signOutEverywhere.mutate();
            }}
            disabled={signOutEverywhere.isPending}
            className="btn-secondary text-danger"
          >
            <LogOut size={15} />
            {t("account.signOutEverywhere")}
          </button>
        </div>
        <p className="mt-3 text-xs text-muted">{t("account.securityNote")}</p>
      </Card>

      {/* Last on the page, deliberately. Nobody arrives here looking for it,
          and it should not sit next to anything routine. */}
      <Card title={t("account.dangerZone")}>
        {pendingDeletion ? (
          <>
            <Notice tone="warning">
              {t("account.deletePending", { date: pendingPurgeAt!.toLocaleDateString() })}
            </Notice>
            <p className="mt-3 text-xs text-muted">{t("account.deletePendingNote")}</p>
            <button
              onClick={() => cancelDeletion.mutate()}
              disabled={cancelDeletion.isPending}
              className="btn-secondary mt-3"
            >
              {cancelDeletion.isPending ? <Spinner /> : null}
              {t("account.deleteCancelButton")}
            </button>
          </>
        ) : confirming ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              requestDeletion.mutate();
            }}
          >
            <p className="text-sm text-muted">{t("account.deleteIntro")}</p>
            <label className="mt-3 block text-sm font-medium" htmlFor="delete-password">
              {t("account.deletePasswordLabel")}
            </label>
            <input
              id="delete-password"
              type="password"
              autoComplete="current-password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              className="input mt-1 max-w-xs"
              required
            />
            <div className="mt-3 flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={requestDeletion.isPending || deletePassword.length === 0}
                className="btn-secondary text-danger"
              >
                {requestDeletion.isPending ? <Spinner /> : <Trash2 size={15} />}
                {t("account.deleteConfirm")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirming(false);
                  setDeletePassword("");
                }}
                className="btn-secondary"
              >
                {t("account.deleteBack")}
              </button>
            </div>
          </form>
        ) : (
          <>
            <p className="text-sm text-muted">{t("account.deleteIntro")}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button onClick={() => setConfirming(true)} className="btn-secondary text-danger">
                <Trash2 size={15} />
                {t("account.deleteButton")}
              </button>
              {/* The public page, which spells out what survives deletion and
                  what does not. Same URL Meta has on file. */}
              <a href="/data-deletion" className="text-xs text-muted underline">
                {t("account.deleteLearnMore")}
              </a>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border/50 py-2 last:border-0">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-1 text-xl font-semibold">{value}</p>
      {hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}
